import { Link } from "react-router-dom";

// Marketing landing page for `/`. Static — no Firebase calls, no auth probes.
// Per-event branding (applyBranding) is deliberately NOT invoked here so the
// platform's own identity always shows on the root URL regardless of which
// event's branding was last applied in a sibling tab.
//
// Layout: sticky top nav → hero → features → how-it-works → contact → footer.
// All copy generic by design — same page for every prospective customer.

const CONTACT_EMAIL = "contact@jc-play.co.uk";
const MAILTO = `mailto:${CONTACT_EMAIL}`;

export function Home(): JSX.Element {
  return (
    <div className="min-h-full bg-bg text-fg">
      <TopNav />
      <Hero />
      <Features />
      <HowItWorks />
      <ContactStrip />
      <Footer />
    </div>
  );
}

function TopNav(): JSX.Element {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-bg/70 border-b border-border/40">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="font-display text-lg tracking-tight">
          <span className="text-accent">JC</span>
          <span className="text-fg">-Play</span>
        </Link>
        <nav className="flex items-center gap-2">
          <a
            href={MAILTO}
            className="hidden sm:inline-flex items-center px-4 py-2 rounded-full text-sm text-fg-muted hover:text-fg transition-colors"
          >
            Contact
          </a>
          <Link
            to="/admin"
            className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-accent text-accent-fg hover:opacity-90 transition-opacity"
          >
            Sign in
          </Link>
        </nav>
      </div>
    </header>
  );
}

function Hero(): JSX.Element {
  return (
    <section className="relative overflow-hidden">
      {/* Backdrop wash — radial accent glow, no images. Self-contained dark
          aesthetic that reads as "modern event tech" without needing
          stock photography. */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "radial-gradient(80% 60% at 50% 0%, rgb(var(--accent) / 0.12), transparent 70%), radial-gradient(60% 50% at 0% 100%, rgb(var(--primary) / 0.10), transparent 70%)",
        }}
      />
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-28 md:pt-32 md:pb-36 text-center">
        <div className="animate-fade-up">
          {/* `animate-fade-up` is a one-shot CSS animation defined in
              index.css — gentle slide-up + fade on first paint. No
              animation library in the eager bundle. */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border/60 bg-bg-elev/60 text-xs text-fg-muted mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-pending" />
            Live event booking platform
          </div>

          <h1 className="font-display text-5xl md:text-7xl leading-[1.05] tracking-tight mb-6">
            Live event booking,
            <br />
            <span className="text-accent">by the square.</span>
          </h1>

          <p className="text-fg-muted text-lg md:text-xl max-w-2xl mx-auto mb-10">
            Sell every square of your venue from a roaming tablet. Display the grid
            on the big screen. Let guests follow along on their phone. Branded
            for every event, in real time.
          </p>

          <div className="flex items-center justify-center gap-3 flex-wrap">
            <a
              href={MAILTO}
              className="inline-flex items-center px-6 py-3 rounded-full text-base font-medium bg-accent text-accent-fg hover:opacity-90 transition-opacity"
            >
              Get in touch
            </a>
            <Link
              to="/admin"
              className="inline-flex items-center px-6 py-3 rounded-full text-base font-medium border border-border hover:border-accent transition-colors"
            >
              Admin sign in
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features(): JSX.Element {
  const features: { title: string; body: string; icon: JSX.Element }[] = [
    {
      title: "Roaming tablet sales",
      body:
        "Agents walk the floor with a tablet, tap a square to claim it, capture the buyer in seconds. Confirmations sync everywhere instantly.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
          <rect x="3" y="4" width="18" height="13" rx="2" />
          <path d="M8 21h8M12 17v4" />
        </svg>
      ),
    },
    {
      title: "Public live view & embed",
      body:
        "Guests scan a QR and watch the grid fill in real time on any phone. Embeddable on the venue's own site with a single iframe snippet.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <path d="M14 14h3v3h-3zM20 14h1v1h-1zM20 17v4M14 20h3M17 20v1" />
        </svg>
      ),
    },
    {
      title: "Big-screen reveals",
      body:
        "Plug the venue display into the truth. Winners animate as you draw them; the rest of the grid keeps ticking around them.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
          <rect x="2" y="4" width="20" height="13" rx="2" />
          <path d="M8 21h8M12 17v4" />
          <path d="M9 10l4 2-4 2z" fill="currentColor" />
        </svg>
      ),
    },
    {
      title: "Per-event branding",
      body:
        "Upload the logo, set the palette, tune the zone colours. Every operator, display and public surface follows your brand within minutes.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
          <circle cx="13" cy="11" r="8" />
          <path d="M5 19l3-3M9 11h1M14 8h1M17 13h1M13 16h1" />
        </svg>
      ),
    },
  ];
  return (
    <section className="border-t border-border/40">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-widest text-fg-muted mb-3">What it does</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">Everything an event needs to sell the floor, live.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-2xl border border-border bg-bg-elev/60 p-6 hover:border-accent/60 transition-colors"
            >
              <div className="text-accent mb-4">{f.icon}</div>
              <div className="font-display text-lg mb-1.5">{f.title}</div>
              <p className="text-sm text-fg-muted leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks(): JSX.Element {
  const steps: { title: string; body: string }[] = [
    {
      title: "Set up your event",
      body: "Upload the venue photo, draw the bookable area, generate the zone grid in seconds. Tweak colours and prices to match the event.",
    },
    {
      title: "Sell on the floor",
      body: "Agents claim squares from their tablet — every claim is transactional, so two agents can never accidentally sell the same square.",
    },
    {
      title: "Reveal & follow",
      body: "Run the big-screen reveals when it's time to draw. Guests, sponsors and stragglers keep up on the public live view from anywhere.",
    },
  ];
  return (
    <section className="border-t border-border/40 bg-bg-elev/30">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-widest text-fg-muted mb-3">How it works</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">Three surfaces. One source of truth.</h2>
        </div>
        <ol className="grid md:grid-cols-3 gap-4">
          {steps.map((s, i) => (
            <li key={s.title} className="rounded-2xl border border-border bg-bg p-6">
              <div className="font-display text-3xl text-accent mb-3 tabular-nums">{String(i + 1).padStart(2, "0")}</div>
              <div className="font-display text-lg mb-1.5">{s.title}</div>
              <p className="text-sm text-fg-muted leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function ContactStrip(): JSX.Element {
  return (
    <section className="border-t border-border/40">
      <div className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-4">
          Running an event? <span className="text-accent">Let's talk.</span>
        </h2>
        <p className="text-fg-muted mb-8 max-w-xl mx-auto">
          Tell us about the venue and the date — we'll get you set up with a branded event on the platform.
        </p>
        <a
          href={MAILTO}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-base font-medium bg-accent text-accent-fg hover:opacity-90 transition-opacity"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M3 7l9 6 9-6" />
          </svg>
          {CONTACT_EMAIL}
        </a>
      </div>
    </section>
  );
}

function Footer(): JSX.Element {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-border/40">
      <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-fg-muted">
        <div>© {year} JC-Play. All rights reserved.</div>
        <div className="flex items-center gap-4">
          <a href={MAILTO} className="hover:text-fg transition-colors">{CONTACT_EMAIL}</a>
          <Link to="/admin" className="hover:text-fg transition-colors">Admin</Link>
        </div>
      </div>
    </footer>
  );
}
